import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  Appointment,
  Events,
  PatientAppointmentsRepositoryService,
  WebSocketService
} from '@xmedical/core';
import { PatientStore } from './patient.store';
/**
 * PatientAppointmentsStore — appointments belonging to the current patient.
 *
 * The cross-patient calendar view is owned by CalendarStore and is
 * unaffected; this store covers the per-patient list shown in the patient
 * profile / appointments widget. Auto-loads on patient change, owns the
 * AppointmentAppointmentChanged push subscription, exposes optimistic add
 * + update.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientAppointmentsStore {
  private patientStore = inject(PatientStore);
  private repo = inject(PatientAppointmentsRepositoryService);
  private websocket = inject(WebSocketService);

  private readonly _items = signal<Appointment[]>([]);
  private readonly _loading = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items().length);

  private currentChannel: string | null = null;
  private readonly eventName = Events.AppointmentAppointmentChanged;
  // Cancels any in-flight `fetch()` HTTP when a newer request arrives.
  private fetchSub?: Subscription;

  constructor() {
    effect(() => {
      const patient = this.patientStore.current();
      const channel = this.patientStore.patientEventsChannelName();

      if (this.currentChannel && this.currentChannel !== channel) {
        this.websocket.unsubscribe(this.currentChannel, this.eventName);
        this.currentChannel = null;
      }

      if (!patient) {
        this.fetchSub?.unsubscribe();
        this._loading.set(false);
        this._items.set([]);
        this._error.set(null);
        return;
      }

      this.fetch(patient.id);

      if (channel && this.currentChannel !== channel) {
        this.websocket.subscribe(channel, this.eventName, () => this.reload());
        this.currentChannel = channel;
      }
    });
  }

  reload(): void {
    const patient = this.patientStore.current();
    if (!patient) return;
    this.fetch(patient.id);
  }

  /**
   * Add an appointment to the current patient. Optimistic — appends and
   * re-sorts immediately, rolls back on error.
   */
  add(appointment: Appointment): Observable<boolean> {
    const patient = this.patientStore.current();
    if (!patient) {
      throw new Error('PatientAppointmentsStore.add() called with no current patient');
    }
    const previous = this._items();
    this._items.set(this.sortByStartDesc([...previous, appointment]));
    return this.repo.addAppointmentToPatient(patient.id, appointment).pipe(
      tap({
        next: () => this.reload(),
        error: error => {
          this._items.set(previous);
          
        }
      })
    );
  }

  /**
   * Update an appointment in place (used by the per-row widget for type +
   * comment edits). Mutation is in-place to mirror existing behaviour and
   * preserve @Input-bound consumers; the list is re-sorted in case start
   * date changes downstream.
   */
  update(appointment: Appointment): Observable<boolean> {
    return this.repo.updateAppointment(appointment).pipe(
      tap({
        next: () => {
          this._items.set(this.sortByStartDesc([...this._items()]));
        }
      })
    );
  }

  private fetch(patientId: number): void {
    this.fetchSub?.unsubscribe();
    this._loading.set(true);
    this._error.set(null);
    this.fetchSub = this.repo.loadPatientAppointments(patientId).subscribe({
      next: appointments => {
        this._items.set(this.sortByStartDesc(appointments));
        this._loading.set(false);
      },
      error: error => {
        this._loading.set(false);
        this._error.set('Could not load patient appointments');
        
      }
    });
  }

  private sortByStartDesc(appointments: Appointment[]): Appointment[] {
    return [...appointments].sort(
      (a, b) => new Date(b.start).getTime() - new Date(a.start).getTime()
    );
  }
}
